import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import { EmptyState } from '@siliconx/shared/ui/state';
import { QueryBoundary } from '@siliconx/shared/ui/QueryBoundary';
import { SubmissionStatusBadge } from '@siliconx/shared/ui/SubmissionStatusBadge';
import { DataTable } from '@siliconx/shared/ui/DataTable';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';

export const Route = createFileRoute('/submissions/')({
  component: SubmissionsPage,
});

async function fetchSubmissions() {
  const res = await api.api.client.submissions.$get();
  await throwIfNotOk(res);
  const { data } = await res.json();
  return data ?? [];
}

type Submission = Awaited<ReturnType<typeof fetchSubmissions>>[number];

const COLUMNS: ColumnDef<Submission, unknown>[] = [
  {
    id: 'assetClass',
    header: 'Asset Class',
    cell: (ctx) => (
      <Link
        to="/submissions/$submissionId"
        params={{ submissionId: ctx.row.original.id }}
        className="font-medium text-brand-600 hover:underline"
      >
        {ctx.row.original.assetClass ?? '—'}
      </Link>
    ),
  },
  {
    id: 'makeModel',
    header: 'Make / Model',
    cell: (ctx) => (
      <span className="text-foreground">
        {[ctx.row.original.make, ctx.row.original.model].filter(Boolean).join(' ') || '—'}
      </span>
    ),
  },
  {
    id: 'serialNumber',
    header: 'Serial Number',
    cell: (ctx) => (
      <span className="font-mono text-xs text-muted-foreground">
        {ctx.row.original.serialNumber ?? '—'}
      </span>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    cell: (ctx) => <SubmissionStatusBadge value={ctx.row.original.status} />,
  },
  {
    id: 'createdAt',
    header: 'Submitted',
    cell: (ctx) => (
      <span className="text-muted-foreground">
        {ctx.row.original.createdAt
          ? new Date(ctx.row.original.createdAt).toLocaleDateString()
          : '—'}
      </span>
    ),
  },
];

function SubmissionsPage() {
  const submissionsQ = useQuery({
    queryKey: qk.submissions.all,
    queryFn: fetchSubmissions,
  });

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        {/* Submissions is a self-evident list page (Earned-Subtitle Rule) — no subtitle. */}
        <PageHeader title="Submissions" pinTo="/submissions" />
        <Link
          to="/assets/submit"
          className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
        >
          Submit Asset
        </Link>
      </div>

      <QueryBoundary
        query={submissionsQ}
        errorTitle="Couldn't load submissions."
        loadingRows={4}
        emptyState={
          <EmptyState
            title="No submissions yet"
            action={
              <Link
                to="/assets/submit"
                className="text-sm font-medium text-brand-600 hover:underline"
              >
                Submit your first asset
              </Link>
            }
          />
        }
      >
        {(submissions) => <DataTable data={submissions} columns={COLUMNS} />}
      </QueryBoundary>
    </div>
  );
}
